/**
 * Product Engraving Block JavaScript
 * Adapted from configurator-init.js engraving functionality
 * Handles popup, text input, preview, and cart integration
 */

(function() {
  'use strict';

  // State management for engraving
  window.ProductEngravingState = {
    enabled: false,
    pendantCount: 1,
    currentVariant: null,
    pendantSize: '1.5 cm',
    engravings: {},
    productData: null,
    blockSettings: {}
  };

  // Engraving product variant IDs (from configurator)
  const ENGRAVING_VARIANTS = {
    '1.5': '45525720596611', // Standard pendant (15mm/1.5cm)
    '2.0': '45525720629379'  // Large pendant (20mm/2.0cm)
  };

  // Track initialization state
  let variantListenersInitialized = false;
  let initializedBlocks = new WeakSet();
  
  // Store pet names globally
  let cachedPetNames = {};
  
  // DO NOT auto-initialize - wait to be called explicitly
  // This ensures HTML elements exist before initialization
  
  // Function to retrieve pet names from UploadKit
  function getPetNames() {
    const petNames = {};
    const pendantCount = window.ProductEngravingState.pendantCount || 1;
    
    // Try multiple sources for pet names
    
    // 1. Check UploadKit store field values
    if (window.UploadKit && window.UploadKit.store && window.UploadKit.store.fieldValues) {
      let petIndex = 1;
      Object.keys(window.UploadKit.store.fieldValues).forEach((parentKey) => {
        const fieldGroup = window.UploadKit.store.fieldValues[parentKey];
        Object.keys(fieldGroup).forEach((fieldKey) => {
          const value = fieldGroup[fieldKey];
          if (value && value.trim() && petIndex <= pendantCount) {
            petNames[petIndex] = value.trim();
            petIndex++;
          }
        });
      });
    }
    
    // 2. Check UploadKit uploadTextFields
    if (window.UploadKit && window.UploadKit.uploadTextFields) {
      for (let i = 1; i <= pendantCount; i++) {
        const possibleKeys = [
          `Pet's Name ${i}`,
          `Pet's Name`,
          `pet_name_${i}`,
          `Pet Name ${i}`,
          `pet-name-${i}`
        ];
        
        for (const key of possibleKeys) {
          if (window.UploadKit.uploadTextFields[key] && !petNames[i]) {
            petNames[i] = window.UploadKit.uploadTextFields[key];
            break;
          }
        }
      }
    }
    
    // 3. Check DOM inputs for pet names
    const petNameInputs = document.querySelectorAll('input[placeholder*="pet" i], input[name*="pet" i]');
    let inputIndex = 1;
    petNameInputs.forEach((input) => {
      if (input.value && input.value.trim() && inputIndex <= pendantCount && !petNames[inputIndex]) {
        petNames[inputIndex] = input.value.trim();
        inputIndex++;
      }
    });
    
    // Cache the pet names
    cachedPetNames = petNames;
    return petNames;
  }
  
  // Expose globally for manual initialization
  window.initializeEngraving = initializeEngraving;

  function initializeEngraving() {
    // Initialize cart integration immediately when engraving block initializes
    // This ensures form handlers are ready before user can submit
    initializeCartIntegration();
    
    const blocks = document.querySelectorAll('[data-engraving-block]');
    
    if (blocks.length === 0) {
      return;
    }
    
    blocks.forEach((block, index) => {
      // Skip if already initialized
      if (initializedBlocks.has(block)) {
        return;
      }
      
      initializeBlock(block);
      initializedBlocks.add(block);
    });
    
    // Set up variant change listeners only once globally
    if (!variantListenersInitialized) {
      document.addEventListener('variant:change', handleVariantChange);
      document.addEventListener('variantChange', handleVariantChange);
      variantListenersInitialized = true;
    }
  }

  function initializeBlock(block) {
    // Get block settings
    const productJson = block.querySelector('[data-product-json]');
    if (!productJson) {
      return;
    }
    
    try {
      const productData = JSON.parse(productJson.textContent);
      window.ProductEngravingState.productData = productData;
      
      // Store block settings
      window.ProductEngravingState.blockSettings = {
        maxChars: parseInt(block.dataset.maxChars) || 9,
        maxLinesSmall: parseInt(block.dataset.maxLinesSmall) || 2,
        maxLinesLarge: parseInt(block.dataset.maxLinesLarge) || 3
      };
      
      // Set up trigger button
      const triggerButton = block.querySelector('[data-engraving-trigger]');
      if (triggerButton) {
        triggerButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          openEngravingInterface();
        });
        
        // Also add a backup click handler to the parent block
        block.addEventListener('click', function(e) {
          if (e.target.closest('[data-engraving-trigger]')) {
            e.preventDefault();
            e.stopPropagation();
            openEngravingInterface();
          }
        });
      }
      
      // Detect initial variant
      if (productData.selected_or_first_available_variant) {
        const initialVariant = productData.selected_or_first_available_variant;
        window.ProductEngravingState.currentVariant = initialVariant;
        window.ProductEngravingState.pendantCount = detectPendantCount(initialVariant);
        window.ProductEngravingState.pendantSize = detectPendantSize(initialVariant);
      } else if (productData.variants && productData.variants.length > 0) {
        // Fallback to first variant if no selected variant
        const firstVariant = productData.variants[0];
        window.ProductEngravingState.currentVariant = firstVariant;
        window.ProductEngravingState.pendantCount = detectPendantCount(firstVariant);
        window.ProductEngravingState.pendantSize = detectPendantSize(firstVariant);
      }
      
      // Load saved state from session storage
      loadSavedState();
      
      // Update button text based on current variant
      updateButtonText();
      
    } catch (error) {
      // Silently handle initialization errors
    }
  }

  function handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;
    
    window.ProductEngravingState.currentVariant = variant;
    
    // Detect pendant count from variant
    const pendantCount = detectPendantCount(variant);
    window.ProductEngravingState.pendantCount = pendantCount;
    
    // Detect pendant size from variant
    const pendantSize = detectPendantSize(variant);
    window.ProductEngravingState.pendantSize = pendantSize;
    
    // Update button text
    updateButtonText();
  }

  function detectPendantCount(variant) {
    if (!variant) {
      return 1;
    }
    
    // Check option1, option2, option3 (Shopify's actual structure)
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    
    // Look for pendant/quantity option
    for (const option of options) {
      const optionStr = String(option);
      
      // First check if it's just a number (like "1", "2", "3", "4")
      if (/^\d+$/.test(optionStr)) {
        const count = parseInt(optionStr);
        if (count >= 1 && count <= 10) { // Reasonable pendant count range
          return count;
        }
      }
      
      // Then check for patterns like "2 Pendants", "3 pendant", "Quantity: 2"
      const optionLower = optionStr.toLowerCase();
      const match = optionLower.match(/(\d+)\s*pendant/) || optionLower.match(/quantity.*(\d+)/);
      if (match) {
        const count = parseInt(match[1]);
        return count;
      }
    }
    
    return 1;
  }

  function detectPendantSize(variant) {
    if (!variant) return '1.5 cm';
    
    // Check option1, option2, option3 (Shopify's actual structure)
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    
    // Look for size option
    for (const option of options) {
      const optionStr = String(option);
      if (optionStr.includes('2.0') || optionStr.includes('2 cm') || optionStr.includes('20mm')) {
        return '2.0 cm';
      }
      if (optionStr.includes('1.5') || optionStr.includes('1.5 cm') || optionStr.includes('15mm')) {
        return '1.5 cm';
      }
    }
    
    return '1.5 cm';
  }

  function updateButtonText() {
    const pendantCount = window.ProductEngravingState.pendantCount;
    
    // Update pendant count text
    const countTexts = document.querySelectorAll('[data-pendant-count-text]');
    countTexts.forEach(el => {
      el.textContent = pendantCount;
    });
    
    // Update button data attribute for styling
    const buttons = document.querySelectorAll('[data-engraving-trigger]');
    buttons.forEach(btn => {
      btn.closest('.product-engraving-block').dataset.pendantCount = pendantCount;
    });
    
    // Update saved indicator visibility
    updateSavedIndicator();
  }

  function updateSavedIndicator() {
    const hasEngraving = Object.values(window.ProductEngravingState.engravings)
      .some(e => e && e.hasText);
    
    const indicators = document.querySelectorAll('[data-engraving-saved]');
    const buttons = document.querySelectorAll('.engraving-trigger-button');
    
    indicators.forEach(indicator => {
      indicator.style.display = hasEngraving ? 'block' : 'none';
    });
    
    buttons.forEach(button => {
      if (hasEngraving) {
        button.classList.add('has-engraving');
      } else {
        button.classList.remove('has-engraving');
      }
    });
  }

  function openEngravingInterface() {
    const pendantCount = window.ProductEngravingState.pendantCount;
    
    // Refresh pet names before opening interface
    getPetNames();
    
    if (pendantCount === 1) {
      // For single pendant, show direct input interface
      openSinglePendantInterface();
    } else {
      // For multiple pendants, show pendant selection grid
      openMultiplePendantInterface();
    }
  }

  function openSinglePendantInterface() {
    const pendantSize = window.ProductEngravingState.pendantSize;
    const maxLines = pendantSize.includes('2.0') 
      ? window.ProductEngravingState.blockSettings.maxLinesLarge 
      : window.ProductEngravingState.blockSettings.maxLinesSmall;
    const maxChars = window.ProductEngravingState.blockSettings.maxChars;
    
    const engravingData = window.ProductEngravingState.engravings[1] || {
      lines: Array(maxLines).fill(''),
      hasText: false
    };
    
    const popupHTML = `
      <div class="engraving-popup-overlay" data-engraving-popup>
        <div class="engraving-popup">
          <div class="engraving-popup-header">
            <h3>Add Engraving on Back</h3>
            <button class="engraving-popup-close" data-popup-close>&times;</button>
          </div>
          <div class="engraving-popup-content">
            <p class="engraving-popup-subtitle">
              Make your necklace one-of-a-kind with a personalized message on the back. 
              Each message is deep laser engraved & ink-filled for maximum visibility.
            </p>
            <!-- Preview -->
            <div class="engraving-popup-preview">
              <img src="https://cdn.shopify.com/s/files/1/0369/9854/5539/files/Blank-PFN-Gold.jpg?v=1750421148"
                   alt="Pendant preview"
                   class="pendant-preview-image">
              <div class="engraving-text-overlay">
                ${Array(maxLines).fill(0).map((_, i) => `
                  <div class="preview-line" data-preview-line="${i + 1}">${engravingData.lines[i] || ''}</div>
                `).join('')}
              </div>
            </div>
            <!-- Input Fields -->
            <div class="engraving-popup-inputs">
              ${Array(maxLines).fill(0).map((_, i) => `
                <div class="engraving-input-group">
                  <label>Line ${i + 1}:</label>
                  <div class="engraving-input-wrapper">
                    <input type="text"
                           class="engraving-popup-input"
                           data-popup-pendant="1"
                           data-popup-line="${i + 1}"
                           maxlength="${maxChars}"
                           placeholder="${maxChars} characters max"
                           value="${engravingData.lines[i] || ''}"
                           autocomplete="off">
                    <span class="engraving-char-counter">${(engravingData.lines[i] || '').length}/${maxChars}</span>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          <div class="engraving-popup-footer">
            <button class="engraving-popup-done" data-popup-done="1">Save Engraving</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    setupPopupEventListeners();
    
    // Focus first input
    setTimeout(() => {
      const firstInput = document.querySelector('.engraving-popup-input');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  function openMultiplePendantInterface() {
    const pendantCount = window.ProductEngravingState.pendantCount;
    
    // Get pet names
    const petNames = getPetNames();
    
    // Create pendant selection grid
    const popupHTML = `
      <div class="engraving-popup-overlay" data-engraving-popup>
        <div class="engraving-popup" style="max-width: 600px;">
          <div class="engraving-popup-header">
            <h3>Add Back Engraving</h3>
            <button class="engraving-popup-close" data-popup-close>&times;</button>
          </div>
          <div class="engraving-popup-content">
            <p class="engraving-popup-subtitle">
              Select a pendant to add engraving. Each pendant can have its own custom message.
            </p>
            <!-- Pendant Selection Grid -->
            <div class="configurator-engraving__pendant-grid">
              ${Array(pendantCount).fill(0).map((_, i) => {
                const pendantNum = i + 1;
                const hasEngraving = window.ProductEngravingState.engravings[pendantNum]?.hasText;
                const petName = petNames[pendantNum] || `Pendant ${pendantNum}`;
                return `
                  <div class="engraving-pendant-box ${hasEngraving ? 'engraving-pendant-box--completed' : ''}"
                       data-pendant-box="${pendantNum}">
                    <h4 class="pendant-box-title">
                      Add engraving on back of <strong>${petName}</strong>
                    </h4>
                    <p class="pendant-box-price">+$10</p>
                    ${hasEngraving ? '<span class="pendant-box-check">✓</span>' : ''}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    setupPopupEventListeners();
  }

  function openPendantEngravingPopup(pendantNum) {
    const pendantSize = window.ProductEngravingState.pendantSize;
    const maxLines = pendantSize.includes('2.0') 
      ? window.ProductEngravingState.blockSettings.maxLinesLarge 
      : window.ProductEngravingState.blockSettings.maxLinesSmall;
    const maxChars = window.ProductEngravingState.blockSettings.maxChars;
    
    const engravingData = window.ProductEngravingState.engravings[pendantNum] || {
      lines: Array(maxLines).fill(''),
      hasText: false
    };
    
    // Get pet names
    const petNames = getPetNames();
    const petName = petNames[pendantNum] || `Pendant ${pendantNum}`;
    const headerText = petName !== `Pendant ${pendantNum}` ? `Engrave ${petName}'s Pendant` : `Engrave Pendant ${pendantNum}`;
    
    const popupHTML = `
      <div class="engraving-popup-overlay" data-engraving-popup data-pendant-popup="${pendantNum}">
        <div class="engraving-popup">
          <div class="engraving-popup-header">
            <h3>${headerText}</h3>
            <button class="engraving-popup-close" data-popup-close>&times;</button>
          </div>
          <div class="engraving-popup-content">
            <!-- Preview -->
            <div class="engraving-popup-preview">
              <img src="https://cdn.shopify.com/s/files/1/0369/9854/5539/files/Blank-PFN-Gold.jpg?v=1750421148"
                   alt="Pendant preview"
                   class="pendant-preview-image">
              <div class="engraving-text-overlay">
                ${Array(maxLines).fill(0).map((_, i) => `
                  <div class="preview-line" data-popup-preview-line="${i + 1}">${engravingData.lines[i] || ''}</div>
                `).join('')}
              </div>
            </div>
            <!-- Input Fields -->
            <div class="engraving-popup-inputs">
              ${Array(maxLines).fill(0).map((_, i) => `
                <div class="engraving-input-group">
                  <label>Line ${i + 1}:</label>
                  <div class="engraving-input-wrapper">
                    <input type="text"
                           class="engraving-popup-input"
                           data-popup-pendant="${pendantNum}"
                           data-popup-line="${i + 1}"
                           maxlength="${maxChars}"
                           placeholder="${maxChars} characters max"
                           value="${engravingData.lines[i] || ''}"
                           autocomplete="off">
                    <span class="engraving-char-counter">${(engravingData.lines[i] || '').length}/${maxChars}</span>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          <div class="engraving-popup-footer">
            <button class="engraving-popup-done" data-popup-done="${pendantNum}">Done</button>
          </div>
        </div>
      </div>
    `;
    
    // Remove existing pendant popup if any
    const existingPopup = document.querySelector('[data-pendant-popup]');
    if (existingPopup) existingPopup.remove();
    
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    setupPopupEventListeners();
    
    // Focus first input
    setTimeout(() => {
      const firstInput = document.querySelector(`[data-pendant-popup="${pendantNum}"] .engraving-popup-input`);
      if (firstInput) firstInput.focus();
    }, 100);
  }

  // Track if global listeners are set up
  let popupListenersInitialized = false;
  
  function setupPopupEventListeners() {
    // Set up global click handler only once
    if (!popupListenersInitialized) {
      document.addEventListener('click', handlePopupClick);
      popupListenersInitialized = true;
    }
    
    // Input handlers for current popup
    const inputs = document.querySelectorAll('.engraving-popup-input');
    inputs.forEach(input => {
      input.addEventListener('input', handleEngravingInput);
    });
  }

  function handlePopupClick(e) {
    // Close popup
    if (e.target.matches('[data-popup-close]')) {
      const popup = e.target.closest('[data-engraving-popup]');
      if (popup) popup.remove();
      updateSavedIndicator();
    }
    
    // Done button
    if (e.target.matches('[data-popup-done]')) {
      const pendantNum = parseInt(e.target.dataset.popupDone);
      saveEngravingFromPopup(pendantNum);
      
      const popup = e.target.closest('[data-engraving-popup]');
      const isPendantPopup = popup.dataset.pendantPopup;
      
      if (isPendantPopup) {
        // This was a pendant-specific popup, go back to grid
        popup.remove();
        
        // Update the pendant box in the grid
        const pendantBox = document.querySelector(`[data-pendant-box="${pendantNum}"]`);
        if (pendantBox) {
          const hasText = window.ProductEngravingState.engravings[pendantNum]?.hasText;
          if (hasText) {
            pendantBox.classList.add('engraving-pendant-box--completed');
            if (!pendantBox.querySelector('.pendant-box-check')) {
              pendantBox.insertAdjacentHTML('beforeend', '<span class="pendant-box-check">✓</span>');
            }
          } else {
            pendantBox.classList.remove('engraving-pendant-box--completed');
            const check = pendantBox.querySelector('.pendant-box-check');
            if (check) check.remove();
          }
        }
      } else {
        // Close the entire popup
        popup.remove();
        updateSavedIndicator();
      }
    }
    
    // Pendant box click
    const pendantBox = e.target.closest('[data-pendant-box]');
    if (pendantBox) {
      const pendantNum = parseInt(pendantBox.dataset.pendantBox);
      openPendantEngravingPopup(pendantNum);
    }
  }

  function handleEngravingInput(e) {
    const value = removeEmojis(e.target.value);
    
    // Update input if emojis were removed
    if (e.target.value !== value) {
      e.target.value = value;
    }
    
    const lineNum = e.target.dataset.popupLine;
    const pendantNum = e.target.dataset.popupPendant;
    
    // Update character counter
    const counter = e.target.parentElement.querySelector('.engraving-char-counter');
    if (counter) {
      counter.textContent = `${value.length}/${window.ProductEngravingState.blockSettings.maxChars}`;
    }
    
    // Update preview
    const previewLine = document.querySelector(`[data-popup-preview-line="${lineNum}"]`) ||
                       document.querySelector(`[data-preview-line="${lineNum}"]`);
    if (previewLine) {
      previewLine.textContent = value;
      if (value) {
        previewLine.style.cssText = 'opacity: 1; visibility: visible; color: #333;';
      }
    }
  }

  function saveEngravingFromPopup(pendantNum) {
    const inputs = document.querySelectorAll(`[data-popup-pendant="${pendantNum}"]`);
    const lines = [];
    let hasText = false;
    
    inputs.forEach(input => {
      const value = input.value.trim();
      lines.push(value);
      if (value) hasText = true;
    });
    
    // Save to state
    window.ProductEngravingState.engravings[pendantNum] = {
      lines: lines,
      hasText: hasText
    };
    
    // Update enabled flag
    const anyEngravings = Object.values(window.ProductEngravingState.engravings)
      .some(e => e && e.hasText);
    window.ProductEngravingState.enabled = anyEngravings;
    
    // Save to session storage
    saveState();
  }

  function removeEmojis(text) {
    // Comprehensive emoji regex pattern (from configurator)
    const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F1FF}]|[\u{1F200}-\u{1F2FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{1FB00}-\u{1FBFF}]/gu;
    return text.replace(emojiRegex, '');
  }

  function saveState() {
    const stateKey = `product_engraving_${window.ProductEngravingState.productData?.id || 'default'}`;
    sessionStorage.setItem(stateKey, JSON.stringify(window.ProductEngravingState));
  }

  function loadSavedState() {
    const stateKey = `product_engraving_${window.ProductEngravingState.productData?.id || 'default'}`;
    const savedState = sessionStorage.getItem(stateKey);
    
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        window.ProductEngravingState.engravings = state.engravings || {};
        window.ProductEngravingState.enabled = state.enabled || false;
        updateSavedIndicator();
      } catch (error) {
        // Silently handle error loading saved state
      }
    }
  }

  // Cart integration - intercept form submission
  function initializeCartIntegration() {
    // Intercept ALL forms to add engraving data when needed
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      // Use capturing phase (third parameter = true) to run BEFORE other handlers
      // This ensures we intercept submission before UploadKit or other handlers
      form.addEventListener('submit', handleFormSubmit, true);
    });
  }

  async function handleFormSubmit(e) {
    // Check if we have any engravings to add
    const hasEngravings = Object.values(window.ProductEngravingState.engravings || {})
      .some(e => e && e.hasText);
    
    // If no engravings, let form submit normally
    if (!hasEngravings) return;
    
    e.preventDefault();
    e.stopImmediatePropagation(); // Stop other handlers from running
    const form = e.target;
    
    // Get main product data from form
    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1);
    
    // Prepare cart items with main product
    const mainItem = {
      id: mainVariantId,
      quantity: quantity,
      properties: {}
    };
    
    // Gather existing form properties (from UploadKit or other sources)
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && key.endsWith(']')) {
        const propName = key.slice(11, -1); // Extract property name
        if (value) {
          mainItem.properties[propName] = value;
        }
      }
    }
    
    // Add UploadKit data if available
    if (window.UploadKit && window.UploadKit.uploadTextFields) {
      Object.keys(window.UploadKit.uploadTextFields).forEach(key => {
        if (window.UploadKit.uploadTextFields[key]) {
          mainItem.properties[key] = window.UploadKit.uploadTextFields[key];
        }
      });
    }
    
    // Add engraving text as properties
    const pendantCount = window.ProductEngravingState.pendantCount;
    const engravings = window.ProductEngravingState.engravings;
    let engravingCount = 0;
    
    Object.keys(engravings).forEach(pendantIndex => {
      const pendant = engravings[pendantIndex];
      if (pendant && pendant.hasText && pendant.lines.length > 0) {
        const engravingText = pendant.lines.filter(line => line.trim()).join(' || ');
        if (pendantCount > 1) {
          mainItem.properties[`Back Engraving ${pendantIndex}`] = engravingText;
        } else {
          mainItem.properties['Back Engraving'] = engravingText;
        }
        engravingCount++;
      }
    });
    
    // Prepare items array
    const items = [mainItem];
    
    // Add engraving product if needed
    if (engravingCount > 0) {
      const engravingVariantId = getEngravingVariantId();
      // Ensure quantity matches actual pendant count for consistency
      const engravingQuantity = Math.max(engravingCount, window.ProductEngravingState.pendantCount || 1);
      items.push({
        id: engravingVariantId,
        quantity: engravingQuantity,
        properties: {
          '_parent_product': window.ProductEngravingState.productData?.title || 'Product'
        }
      });
    }
    
    // Submit to cart
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      
      if (response.ok) {
        // Clear saved state after successful add
        const stateKey = `product_engraving_${window.ProductEngravingState.productData?.id || 'default'}`;
        sessionStorage.removeItem(stateKey);
        
        // Trigger cart update events
        document.dispatchEvent(new CustomEvent('cart:build'));
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        }, 100);
      } else {
        // If failed, submit original form
        form.submit();
      }
    } catch (error) {
      // If cart add fails, fall back to normal form submission
      form.submit();
    }
  }

  function getEngravingVariantId() {
    const pendantSize = window.ProductEngravingState.pendantSize;
    
    if (pendantSize.includes('2.0')) {
      return ENGRAVING_VARIANTS['2.0'];
    } else {
      return ENGRAVING_VARIANTS['1.5'];
    }
  }

  // Initialize cart integration immediately when script loads
  // Use capturing phase to intercept BEFORE other handlers like UploadKit
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartIntegration);
  } else {
    // DOM already loaded, initialize immediately
    initializeCartIntegration();
  }

})();